'use client';

import { useOverlay } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import type { Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import type { ReactNode} from 'react';
import { useRef } from 'react';
import styled from 'styled-components';

import { Spacing, useMeasure } from '@zoomit/primitives';
import { elevation, theme } from '@zoomit/zoomit-ds';

import { useSafeMotion } from '../../hooks/useSafeMotion';

type Props = {
  isSearchActive: boolean;
  setIsSearchActive: (b: boolean) => void;
  children: ReactNode;
};

const SearchDesktopBody = (props: Props) => {
  const { isSearchActive, setIsSearchActive, children } = props;
  const searchRef = useRef<HTMLDivElement>(null);
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen: isSearchActive,
      onClose: () => setIsSearchActive(false),
      isDismissable: true,
    },
    searchRef,
  );

  const {
    ref: searchWrapperRef,
    rect: { height },
  } = useMeasure<HTMLDivElement>();

  const searchInstanceVariants: Variants = {
    open: {
      height: 'auto',
    },
    close: {
      height: 0,
    },
  };

  const safeMotion = useSafeMotion({
    variants: searchInstanceVariants,
    animate: { height: isSearchActive ? height : 0 },
    exit: 'close',
    initial: 'close',
  });

  return (
    <SearchWrapper
      transition={{
        type: 'spring',
        damping: 100,
        stiffness: 1000,
      }}
      {...mergeProps(overlayProps, safeMotion, underlayProps)}
      ref={searchRef}
    >
      <div ref={searchWrapperRef}>{children}</div>
    </SearchWrapper>
  );
};

export default SearchDesktopBody;

const SearchWrapper = styled(motion.div)`
  background: var(${theme.background2});
  position: absolute;
  top: ${Spacing.x16}rem;
  left: 0;
  width: 100%;
  overflow: hidden;
  ${elevation};
`;
